import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import SEO from './components/SEO';
import {
  FaSearch,
  FaPlay,
  FaPause,
  FaStepForward,
  FaStepBackward,
  FaExpand,
  FaTimes,
  FaChevronLeft,
  FaChevronRight,
} from 'react-icons/fa';
import YouTube from 'react-youtube';
import transliterateText from './utils/transliteration';
import playlistsData from './assets/livestreaming/lean/playlists-lean.json';
import playlistItemsMap from './assets/livestreaming/lean/playlist-items-lean.json';
import videosCatalog from './assets/livestreaming/lean/videos-lean.json';
import './video.css';

const MIN_COLUMN_WIDTHS = {
  playlist: 220,
  player: 420,
  video: 240,
};

const DEFAULT_COLUMN_FLEX = {
  playlist: 1,
  player: 2,
  video: 1,
};

const selectImageFromThumbnails = (thumbs = {}) =>
  thumbs.maxres?.url || thumbs.standard?.url || thumbs.high?.url || thumbs.medium?.url || thumbs.default?.url || '';

const resolvePlaylistEntryId = (entry) => {
  if (!entry) return '';
  if (typeof entry === 'string') return entry;
  return entry.id || entry.videoId || '';
};

const buildPlaylistLookup = (items) => {
  const lookup = {};
  Object.entries(items || {}).forEach(([playlistId, entries]) => {
    const ids = new Set();
    (entries || []).forEach((entry) => {
      const id = resolvePlaylistEntryId(entry);
      if (id) ids.add(id);
    });
    lookup[playlistId] = ids;
  });
  return lookup;
};

const formatDate = (value) => {
  if (!value) return '';
  const parsed = Date.parse(value);
  if (Number.isNaN(parsed)) return value;
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(parsed);
};

const shuffleArray = (items = []) => {
  const array = [...items];
  for (let i = array.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
};

const buildSearchableText = (...values) => {
  const combined = values
    .filter(Boolean)
    .map((value) => String(value))
    .join(' ');
  const latinized = transliterateText(combined);
  return `${combined} ${latinized}`.toLowerCase();
};

const parseDuration = (value) => {
  if (!value) return 0;
  const matches = value.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
  if (!matches) return 0;
  const [, hours, minutes, seconds] = matches;
  return (Number(hours) || 0) * 3600 + (Number(minutes) || 0) * 60 + (Number(seconds) || 0);
};

const formatDurationLabel = (value) => {
  const totalSeconds = parseDuration(value);
  if (!totalSeconds) return '0:00';
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

const parseBooleanParam = (value, fallback) => {
  if (value === null || value === undefined) return fallback;
  const normalized = String(value).trim().toLowerCase();
  if (['1', 'true', 'yes', 'on'].includes(normalized)) return true;
  if (['0', 'false', 'no', 'off'].includes(normalized)) return false;
  return fallback;
};

export default function Video() {
  const defaultAutoNext = true;
  const [autoPlayNext, setAutoPlayNext] = useState(() => {
    if (typeof window === 'undefined') return defaultAutoNext;
    const params = new URLSearchParams(window.location.search || '');
    if (!params.has('autonext')) return defaultAutoNext;
    return parseBooleanParam(params.get('autonext'), defaultAutoNext);
  });
  const videoEntries = useMemo(() => {
    return Object.values(videosCatalog || {})
      .map((video) => {
        const entry = {
          id: video.id,
          title: video.title || 'Untitled video',
          thumbnails: video.thumbnails || {},
          tags: Array.isArray(video.tags) ? video.tags : [],
          publishedAt: video.publishedAt,
          duration: video.duration,
          channelTitle: video.channelTitle || 'GCC Worship Platform',
        };
        return {
          ...entry,
          durationSeconds: parseDuration(entry.duration),
          image: selectImageFromThumbnails(entry.thumbnails),
          searchText: buildSearchableText(entry.title, entry.channelTitle, entry.tags.join(' ')),
        };
      })
      .sort((a, b) => {
        const left = Date.parse(a.publishedAt) || 0;
        const right = Date.parse(b.publishedAt) || 0;
        return right - left;
      });
  }, []);

  const playlistEntries = useMemo(() => {
    return (playlistsData || []).map((playlist) => ({
      ...playlist,
      image: selectImageFromThumbnails(playlist.thumbnails || {}),
      count: playlist.videoIds?.length || playlist.itemCount || 0,
      searchText: buildSearchableText(playlist.title),
    }));
  }, []);

  const playlistLookup = useMemo(() => buildPlaylistLookup(playlistItemsMap), []);
  const initialRandomVideos = useMemo(() => shuffleArray(videoEntries), [videoEntries]);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPlaylistId, setSelectedPlaylistId] = useState('');
  const [columnFlex, setColumnFlex] = useState({ ...DEFAULT_COLUMN_FLEX });
  const [dragState, setDragState] = useState(null);
  const [playerVideoId, setPlayerVideoId] = useState(() => initialRandomVideos[0]?.id || '');
  const [playerState, setPlayerState] = useState(null);
  const [queueIndex, setQueueIndex] = useState(0);
  const playerRef = useRef(null);
  const gridRef = useRef(null);
  const playerSectionRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [pendingPlayId, setPendingPlayId] = useState(null);
  const [playerReady, setPlayerReady] = useState(false);
  const [isMetadataVisible, setIsMetadataVisible] = useState(false);
  const [controlsOpen, setControlsOpen] = useState(false);
  const [bottomPanelOpen, setBottomPanelOpen] = useState(true);
  const [randomPlayNext, setRandomPlayNext] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);
  const [isExploreOpen, setIsExploreOpen] = useState(false);
  const userGestureRef = useRef(false);
  const [playlistCollapsed, setPlaylistCollapsed] = useState(false);
  const [playerCollapsed, setPlayerCollapsed] = useState(false);
  const [videoCollapsed, setVideoCollapsed] = useState(false);
  const playlistHeightCollapsed = false;
  const playerHeightCollapsed = false;
  const videoHeightCollapsed = false;
  const hasFilters = Boolean(selectedPlaylistId || searchQuery.trim());

  const markUserGesture = useCallback(() => {
    userGestureRef.current = true;
  }, []);

  const ensureMobileAutoplay = useCallback(() => {
    if (!isMobileView) return;
  }, [isMobileView]);

  const [needsUserPlay, setNeedsUserPlay] = useState(false);
  const pendingAutoPlayIdRef = useRef(null);

  const scheduleAutoplayFallbackCheck = useCallback((videoId) => {
    if (!videoId) return;
    const player = playerRef.current;
    if (!player || typeof player.getPlayerState !== 'function') return;
    pendingAutoPlayIdRef.current = videoId;
    window.setTimeout(() => {
      if (pendingAutoPlayIdRef.current !== videoId) return;
      const state = player.getPlayerState();
      if (state !== 1) {
        setNeedsUserPlay(true);
      }
    }, 700);
  }, []);

  useEffect(() => {
    if (!isMobileView) return;
    setPlaylistCollapsed(false);
    setPlayerCollapsed(false);
    setVideoCollapsed(false);
    setIsExploreOpen(true);
  }, [isMobileView]);

  const videoEntryMap = useMemo(() => {
    const map = {};
    videoEntries.forEach((video) => {
      if (video?.id) {
        map[video.id] = video;
      }
    });
    return map;
  }, [videoEntries]);

  const filteredPlaylists = useMemo(() => {
    if (!searchQuery) return playlistEntries;
    const normalized = searchQuery.trim().toLowerCase();
    const transliterated = normalized ? transliterateText(normalized) : '';
    return playlistEntries.filter((playlist) => {
      const haystack = playlist.searchText || '';
      return (
        (normalized && haystack.includes(normalized)) ||
        (transliterated && haystack.includes(transliterated))
      );
    });
  }, [playlistEntries, searchQuery]);

  const filteredVideos = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    const transliteratedQuery = normalizedQuery ? transliterateText(normalizedQuery) : '';
    const hasQuery = Boolean(normalizedQuery);
    const playlistSet = !hasQuery && selectedPlaylistId && playlistLookup[selectedPlaylistId]
      ? playlistLookup[selectedPlaylistId]
      : null;

    if (!playlistSet && !normalizedQuery) {
      return initialRandomVideos;
    }

    let pool = videoEntries;
    if (playlistSet) {
      pool = pool.filter((video) => playlistSet.has(video.id));
    }
    if (normalizedQuery) {
      pool = pool.filter((video) => {
        const haystack = video.searchText || '';
        return (
          (normalizedQuery && haystack.includes(normalizedQuery)) ||
          (transliteratedQuery && haystack.includes(transliteratedQuery))
        );
      });
    }

    return [...pool].sort((a, b) => {
      const left = Date.parse(a.publishedAt) || 0;
      const right = Date.parse(b.publishedAt) || 0;
      return right - left;
    });
  }, [videoEntries, playlistLookup, selectedPlaylistId, searchQuery, initialRandomVideos]);

  const selectedPlaylistVideos = useMemo(() => {
    if (!selectedPlaylistId) return [];
    const entries = playlistItemsMap?.[selectedPlaylistId] || [];
    return entries
      .map((entry) => videoEntryMap[resolvePlaylistEntryId(entry)])
      .filter(Boolean);
  }, [selectedPlaylistId, videoEntryMap]);

  const playlistItemCount = selectedPlaylistId ? (playlistItemsMap?.[selectedPlaylistId]?.length || 0) : 0;
  const queueVideo = useCallback(
    (index) => {
      if (!filteredVideos.length) {
        setPlayerVideoId('');
        setQueueIndex(0);
        return;
      }
      const bounded = Math.max(0, Math.min(filteredVideos.length - 1, index));
      const video = filteredVideos[bounded];
      if (!video) return;
      setQueueIndex(bounded);
      setPlayerVideoId(video.id);
      setPendingPlayId(video.id);
    },
    [filteredVideos],
  );

  const videoListItems = useMemo(() => {
    const hasSearch = searchQuery.trim().length > 0;
    if (hasSearch) return filteredVideos;
    if (selectedPlaylistId) return selectedPlaylistVideos;
    return filteredVideos;
  }, [searchQuery, filteredVideos, selectedPlaylistId, selectedPlaylistVideos]);

  const buildColumnTemplate = (collapsed, minWidth, flexValue) =>
    collapsed ? '80px' : `minmax(${minWidth}px, ${flexValue}fr)`;
  const playlistTemplate = buildColumnTemplate(playlistCollapsed, MIN_COLUMN_WIDTHS.playlist, columnFlex.playlist);
  const playerTemplate = buildColumnTemplate(playerCollapsed, MIN_COLUMN_WIDTHS.player, columnFlex.player);
  const videoTemplate = buildColumnTemplate(videoCollapsed, MIN_COLUMN_WIDTHS.video, columnFlex.video);
  const columnStyle = {
    '--youtube-playlist-col': playlistTemplate,
    '--youtube-player-col': playerTemplate,
    '--youtube-video-col': videoTemplate,
  };

  useEffect(() => {
    if (!filteredVideos.length) {
      setPlayerVideoId('');
      setQueueIndex(0);
      return;
    }
    if (!playerVideoId) {
      queueVideo(0);
      return;
    }
    const matchIndex = filteredVideos.findIndex((video) => video.id === playerVideoId);
    if (matchIndex >= 0 && matchIndex !== queueIndex) {
      setQueueIndex(matchIndex);
      return;
    }
    if (matchIndex < 0) {
      queueVideo(0);
    }
  }, [filteredVideos, playerVideoId, queueIndex, queueVideo]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const updateMetadataVisibility = () => {
      setIsMetadataVisible(window.innerWidth > 768);
    };
    updateMetadataVisibility();
    window.addEventListener('resize', updateMetadataVisibility);
    return () => {
      window.removeEventListener('resize', updateMetadataVisibility);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleKeyToggle = (event) => {
      if (event.key === 'Control' && !event.repeat && !event.altKey && !event.metaKey) {
        setControlsOpen((prev) => !prev);
        setBottomPanelOpen((prev) => !prev);
        return;
      }
      if (event.key === 'Escape' && controlsOpen) {
        setControlsOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyToggle);
    return () => window.removeEventListener('keydown', handleKeyToggle);
  }, [controlsOpen]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const headerLogo = document.querySelector('.header-logo');
    if (!headerLogo) return undefined;
    const previousCursor = headerLogo.style.cursor;
    headerLogo.style.cursor = 'pointer';
    const handleHeaderClick = (event) => {
      event.preventDefault();
      setControlsOpen((prev) => !prev);
      setBottomPanelOpen((prev) => !prev);
    };
    headerLogo.addEventListener('click', handleHeaderClick);
    return () => {
      headerLogo.removeEventListener('click', handleHeaderClick);
      headerLogo.style.cursor = previousCursor;
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleResize = () => {
      setIsMobileView(window.matchMedia('(max-width: 900px)').matches);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    const params = new URLSearchParams(window.location.search || '');
    if (autoPlayNext === defaultAutoNext) {
      params.delete('autonext');
    } else {
      params.set('autonext', autoPlayNext ? '1' : '0');
    }
    const nextSearch = params.toString();
    const newUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}${window.location.hash}`;
    window.history.replaceState({}, '', newUrl);
    return () => window.removeEventListener('resize', handleResize);
  }, [autoPlayNext, defaultAutoNext]);

  const currentVideo = videoEntryMap[playerVideoId] || null;
  const tagsPlaceholderText = currentVideo
    ? 'No tags available yet for this video.'
    : 'Select a video to display tags and metadata.';
  const metadataPanelClassName = `video-metadata-panel${isMetadataVisible ? '' : ' mobile-collapsed'}`;
  const handlePlaylistSelect = (playlistId) => {
    markUserGesture();
    if (selectedPlaylistId === playlistId) {
      setSelectedPlaylistId('');
    } else {
      setSelectedPlaylistId(playlistId);
    }
  };

  const handleVideoSelect = (videoId) => {
    markUserGesture();
    const newIndex = filteredVideos.findIndex((video) => video.id === videoId);
      if (newIndex >= 0) {
        queueVideo(newIndex);
      }
  };

  const attemptPlay = useCallback((player) => {
    if (!player || typeof player.playVideo !== 'function') return;
    ensureMobileAutoplay(player);
    const tryPlay = (attempt = 0) => {
      try {
        player.playVideo();
      } catch (error) {
        return;
      }
      if (attempt >= 2) return;
      const delay = attempt === 0 ? 200 : 600;
      window.setTimeout(() => {
        let state = null;
        try {
          state = typeof player.getPlayerState === 'function' ? player.getPlayerState() : null;
        } catch (error) {
          return;
        }
        if (state !== 1) {
          tryPlay(attempt + 1);
        }
      }, delay);
    };
    tryPlay();
  }, []);

  const canSendPlayerCommands = (player) => {
    if (!player) return false;
    if (typeof player.playVideo !== 'function') return false;
    if (typeof player.getIframe !== 'function') return false;
    return Boolean(player.getIframe());
  };

  const handlePlayerReady = useCallback((event) => {
    playerRef.current = event.target;
    setPlayerReady(true);
  }, []);

  const handlePlayerStateChange = useCallback((event) => {
    setPlayerState(event.data);
    if (event.data === 1) {
      setNeedsUserPlay(false);
      pendingAutoPlayIdRef.current = null;
    }
    if (event.data === 1 && pendingPlayId) {
      setPendingPlayId(null);
    }
    if (event.data === 5 && pendingPlayId) {
      const player = playerRef.current;
      if (canSendPlayerCommands(player)) {
        attemptPlay(player);
      }
    }
  }, [attemptPlay, pendingPlayId]);

  useEffect(() => {
    if (!pendingPlayId || !playerReady) return;
    const player = playerRef.current;
    if (!canSendPlayerCommands(player)) return;
    const currentId = typeof player.getVideoData === 'function' ? player.getVideoData()?.video_id : null;
    if (pendingPlayId && pendingPlayId !== currentId && typeof player.loadVideoById === 'function') {
      player.loadVideoById(pendingPlayId);
    }
    attemptPlay(player);
  }, [pendingPlayId, playerReady, attemptPlay]);

  useEffect(() => {
    if (!playerVideoId) return;
    scheduleAutoplayFallbackCheck(playerVideoId);
  }, [playerVideoId, scheduleAutoplayFallbackCheck]);

  const handlePlayPause = () => {
    markUserGesture();
    const player = playerRef.current;
    if (!player) return;
    if (playerState === 1) {
      player.pauseVideo();
    } else {
      ensureMobileAutoplay(player);
      player.playVideo();
    }
  };

  const handleResumeAutoplay = () => {
    const player = playerRef.current;
    if (!player) return;
    markUserGesture();
    const nextId = pendingAutoPlayIdRef.current || playerVideoId;
    if (nextId && typeof player.loadVideoById === 'function') {
      player.loadVideoById(nextId);
    }
    player.unMute?.();
    setNeedsUserPlay(false);
    pendingAutoPlayIdRef.current = null;
    player.playVideo();
  };

  const getRandomQueueIndex = useCallback((length, excludeIndex) => {
    if (length <= 1) return 0;
    let nextIndex = excludeIndex;
    let guard = 0;
    while (nextIndex === excludeIndex && guard < 8) {
      nextIndex = Math.floor(Math.random() * length);
      guard += 1;
    }
    if (nextIndex === excludeIndex) {
      return (excludeIndex + 1) % length;
    }
    return nextIndex;
  }, []);

  const handleNext = () => {
    markUserGesture();
    if (!filteredVideos.length) return;
    const nextIndex = randomPlayNext
      ? getRandomQueueIndex(filteredVideos.length, queueIndex)
      : queueIndex + 1;
    queueVideo(nextIndex);
  };

  const playQueuedVideo = useCallback((videoId) => {
    if (!videoId) return;
    const player = playerRef.current;
    if (!canSendPlayerCommands(player)) return;
    if (typeof player.loadVideoById === 'function') {
      player.loadVideoById(videoId);
    }
    attemptPlay(player);
  }, [attemptPlay]);

  const handleAutoAdvance = useCallback(() => {
    if (!autoPlayNext || !filteredVideos.length) return;
    const nextIndex = randomPlayNext
      ? getRandomQueueIndex(filteredVideos.length, queueIndex)
      : (queueIndex + 1) % filteredVideos.length;
    const nextVideo = filteredVideos[nextIndex];
    setNeedsUserPlay(false);
    queueVideo(nextIndex);
    playQueuedVideo(nextVideo?.id);
    scheduleAutoplayFallbackCheck(nextVideo?.id);
  }, [
    autoPlayNext,
    scheduleAutoplayFallbackCheck,
    filteredVideos,
    queueIndex,
    playQueuedVideo,
    queueVideo,
    randomPlayNext,
    getRandomQueueIndex,
  ]);

  const handlePrevious = () => {
    markUserGesture();
    if (!filteredVideos.length) return;
    queueVideo(queueIndex - 1);
  };

  const handlePlayerEnd = () => {
    handleAutoAdvance();
  };

  const toggleFullscreen = async () => {
    if (typeof document === 'undefined') return;
    if (!isFullscreen) {
      try {
        await document.documentElement.requestFullscreen?.();
      } catch (error) {
        console.error('Fullscreen request failed', error);
      }
    } else {
      try {
        await document.exitFullscreen?.();
      } catch (error) {
        console.error('Exit fullscreen failed', error);
      }
    }
  };

  const handleResizeStart = useCallback(
    (edge) => (event) => {
      if (event.button !== 0) return;
      event.preventDefault();
      setDragState({
        edge,
        startX: event.clientX,
        startFlex: { ...columnFlex },
      });
    },
    [columnFlex],
  );

  useEffect(() => {
    if (!dragState || !gridRef.current) return undefined;

    const handlePointerMove = (event) => {
      if (!gridRef.current) return;
      const gridRect = gridRef.current.getBoundingClientRect();
      const gridWidth = gridRect.width;
      if (!gridWidth) return;
      const { edge, startX, startFlex } = dragState;
      const totalFlex = startFlex.playlist + startFlex.player + startFlex.video;
      const deltaX = event.clientX - startX;
      const deltaFlex = (deltaX / gridWidth) * totalFlex;

      if (edge === 'playlist-player') {
        if (playlistCollapsed || playerCollapsed) return;
        const boundaryTotal = startFlex.playlist + startFlex.player;
        const playlistMin = (MIN_COLUMN_WIDTHS.playlist / gridWidth) * totalFlex;
        const playerMin = (MIN_COLUMN_WIDTHS.player / gridWidth) * totalFlex;
        const rawPlaylist = startFlex.playlist + deltaFlex;
        const clampedPlaylist = Math.min(
          Math.max(rawPlaylist, playlistMin),
          boundaryTotal - playerMin
        );
        const newPlayer = boundaryTotal - clampedPlaylist;
        setColumnFlex((prev) => ({
          ...prev,
          playlist: clampedPlaylist,
          player: newPlayer,
        }));
      } else if (edge === 'player-video') {
        if (playerCollapsed || videoCollapsed) return;
        const boundaryTotal = startFlex.player + startFlex.video;
        const playerMin = (MIN_COLUMN_WIDTHS.player / gridWidth) * totalFlex;
        const videoMin = (MIN_COLUMN_WIDTHS.video / gridWidth) * totalFlex;
        const rawPlayer = startFlex.player + deltaFlex;
        const clampedPlayer = Math.min(
          Math.max(rawPlayer, playerMin),
          boundaryTotal - videoMin
        );
        const newVideo = boundaryTotal - clampedPlayer;
        setColumnFlex((prev) => ({
          ...prev,
          player: clampedPlayer,
          video: newVideo,
        }));
      }
    };

    const handlePointerUp = () => {
      setDragState(null);
    };

    window.addEventListener('mousemove', handlePointerMove);
    window.addEventListener('mouseup', handlePointerUp);
    window.addEventListener('mouseleave', handlePointerUp);
    return () => {
      window.removeEventListener('mousemove', handlePointerMove);
      window.removeEventListener('mouseup', handlePointerUp);
      window.removeEventListener('mouseleave', handlePointerUp);
    };
  }, [dragState, playlistCollapsed, playerCollapsed, videoCollapsed]);

  useEffect(() => {
    const className = 'video-fullscreen';
    if (typeof document === 'undefined') return undefined;
    const body = document.body;
    if (isFullscreen) {
      body.classList.add(className);
    } else {
      body.classList.remove(className);
    }
    return () => {
      body.classList.remove(className);
    };
  }, [isFullscreen]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const handler = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };
    document.addEventListener('fullscreenchange', handler);
    document.addEventListener('webkitfullscreenchange', handler);
    document.addEventListener('mozfullscreenchange', handler);
    document.addEventListener('MSFullscreenChange', handler);
    return () => {
      document.removeEventListener('fullscreenchange', handler);
      document.removeEventListener('webkitfullscreenchange', handler);
      document.removeEventListener('mozfullscreenchange', handler);
      document.removeEventListener('MSFullscreenChange', handler);
    };
  }, []);

  const emptyState = !filteredVideos.length;

  return (
    <div className="youtube-page">
      <SEO
        title="Video"
        description="Curated GCC YouTube library with search, queue, and controls."
      />
      <div className={`video-top-controls${controlsOpen ? ' open' : ''}`}>
        <div className="video-top-controls-inner">
          <div className="video-top-controls-header">
            <span>Playback controls</span>
            <button
              type="button"
              className="video-top-controls-close"
              onClick={() => setControlsOpen(false)}
              aria-label="Close controls"
            >
              <FaTimes />
            </button>
          </div>
          <div className="video-top-controls-actions">
            <button
              type="button"
              className={`ghost auto-next-toggle ${autoPlayNext ? 'on' : ''}`}
              onClick={() => setAutoPlayNext((prev) => !prev)}
              aria-pressed={autoPlayNext}
              aria-label="Toggle auto play next"
              title="Auto play next"
            >
              Auto Next
            </button>
            <button
              type="button"
              className={`ghost auto-next-toggle ${randomPlayNext ? 'on' : ''}`}
              onClick={() => setRandomPlayNext((prev) => !prev)}
              aria-pressed={randomPlayNext}
              aria-label="Toggle random play"
              title="Random play"
            >
              Random
            </button>
          </div>
        </div>
      </div>

      <section className="youtube-master-section">
        <div className="youtube-main" style={columnStyle} ref={gridRef}>
          <aside className={`playlist-column ${playlistCollapsed || playlistHeightCollapsed ? 'collapsed-panel' : ''}`}>
            {!playlistCollapsed && !playerCollapsed && (
              <div
                className="panel-handle"
                role="separator"
                aria-orientation="vertical"
                aria-label="Resize playlist panel"
                onMouseDown={handleResizeStart('playlist-player')}
              />
            )}
            <header className="playlist-header">
              <div>
                <h3>Playlists</h3>
              </div>
              <div className="panel-header-actions">
                {hasFilters && (
                  <button
                    type="button"
                    className="ghost clear-filters"
                    onClick={() => {
                      setSelectedPlaylistId('');
                      setSearchQuery('');
                    }}
                  >
                    Clear filters
                  </button>
                )}
                {!isMobileView && (
                  <button
                    type="button"
                    className="panel-collapse-toggle"
                    onClick={() => setPlaylistCollapsed((prev) => !prev)}
                    aria-label={playlistCollapsed ? 'Expand playlists panel' : 'Collapse playlists panel'}
                  >
                    {playlistCollapsed ? <FaChevronRight /> : <FaChevronLeft />}
                  </button>
                )}
              </div>
            </header>
            <div className={`panel-content ${playlistCollapsed || playlistHeightCollapsed ? 'collapsed' : ''}`}>
              <div className="playlist-list">
                {filteredPlaylists.map((playlist) => (
                  <button
                    type="button"
                    className={`playlist-card ${selectedPlaylistId === playlist.id ? 'active' : ''}`}
                    key={playlist.id}
                    onClick={() => handlePlaylistSelect(playlist.id)}
                  >
                    <div className="playlist-thumb" style={{ backgroundImage: `url(${playlist.image})` }} />
                    <div className="playlist-info">
                      <strong>{playlist.title}</strong>
                      <span className="playlist-count">{playlist.count || 0} videos</span>
                    </div>
                  </button>
                ))}
              </div>
              {!selectedPlaylistId && (
                <div className="playlist-hint">
                  Random lineup by default; pick a playlist to lock the queue.
                </div>
              )}
            </div>
          </aside>

          <div
            ref={playerSectionRef}
            className={`player-column ${playerCollapsed || playerHeightCollapsed ? 'collapsed-panel' : ''}`}
          >
            {!playerCollapsed && !videoCollapsed && (
              <div
                className="panel-handle"
                role="separator"
                aria-orientation="vertical"
                aria-label="Resize player and video panels"
                onMouseDown={handleResizeStart('player-video')}
              />
            )}
            {!isMobileView && (
              <div className="player-header">
                <h3>Player</h3>
                <button
                  type="button"
                  className="panel-collapse-toggle"
                  onClick={() => setPlayerCollapsed((prev) => !prev)}
                  aria-label={playerCollapsed ? 'Expand player panel' : 'Collapse player panel'}
                >
                  {playerCollapsed ? <FaChevronRight /> : <FaChevronLeft />}
                </button>
              </div>
            )}
            <div className={`panel-content ${playerCollapsed || playerHeightCollapsed ? 'collapsed' : ''}`}>
              <div className="player-frame">
              {currentVideo ? (
                <YouTube
                    videoId={currentVideo.id}
                    opts={{
                      width: '100%',
                      height: '100%',
                  playerVars: {
                    controls: 1,
                    rel: 0,
                    modestbranding: 1,
                    disablekb: 0,
                    autoplay: 1,
                    mute: 1,
                    cc_load_policy: 0,
                    fs: 1,
                    playsinline: 1,
                  },
                }}
                    onReady={handlePlayerReady}
                    onStateChange={handlePlayerStateChange}
                    onEnd={handlePlayerEnd}
                    className="youtube-iframe"
                    style={{ width: '100%', height: '100%' }}
                  />
                ) : (
                  <div className="player-placeholder">Pick a video to start playing.</div>
              )}
              </div>
              <button
                type="button"
                className="metadata-toggle"
                aria-controls="videoMetadataPanel"
                aria-expanded={isMetadataVisible}
                onClick={() => setIsMetadataVisible((prev) => !prev)}
              >
                {isMetadataVisible ? 'Hide video details' : 'Show video details'}
              </button>
              <div className={metadataPanelClassName} id="videoMetadataPanel">
                <div
                  className="metadata-thumb"
                  style={{
                    backgroundImage: `url(${selectImageFromThumbnails(currentVideo?.thumbnails || {})})`,
                  }}
                />
                <div className="metadata-content">
                  <div className="metadata-title">
                    <strong>
                      {currentVideo?.title || 'Pick a video to view metadata details.'}
                    </strong>
                  </div>
                  <div className="metadata-grid">
                    <div className="metadata-row">
                      <span className="metadata-label">Duration</span>
                      <span className="metadata-value">
                        {currentVideo ? formatDurationLabel(currentVideo.duration) : '0:00'}
                      </span>
                    </div>
                    <div className="metadata-row">
                      <span className="metadata-label">Published</span>
                      <span className="metadata-value">
                        {currentVideo ? formatDate(currentVideo.publishedAt) : 'TBD'}
                      </span>
                    </div>
                    <div className="metadata-row">
                      <span className="metadata-label">Channel</span>
                      <span className="metadata-value">
                        {currentVideo?.channelTitle || 'GCC Worship Platform'}
                      </span>
                    </div>
                  </div>
                  {currentVideo?.tags?.length ? (
                    <div className="metadata-tags">
                      {currentVideo.tags.slice(0, 10).map((tag) => (
                        <span key={tag} className="metadata-tag">
                          {tag}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="metadata-placeholder">{tagsPlaceholderText}</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          <aside className={`video-column ${videoCollapsed || videoHeightCollapsed ? 'collapsed-panel' : ''}`}>
            <header className="playlist-header">
              <div>
                <h3>Playlist videos</h3>
              </div>
              {!isMobileView && (
                <div className="panel-header-actions">
                  <button
                    type="button"
                    className="panel-collapse-toggle"
                    onClick={() => setVideoCollapsed((prev) => !prev)}
                    aria-label={videoCollapsed ? 'Expand playlist videos panel' : 'Collapse playlist videos panel'}
                  >
                    {videoCollapsed ? <FaChevronLeft /> : <FaChevronRight />}
                  </button>
                </div>
              )}
            </header>
            <div className={`panel-content ${videoCollapsed || videoHeightCollapsed ? 'collapsed' : ''}`}>
              <div className="video-list-grid">
                {videoListItems.length ? (
                  videoListItems.map((video) => (
                    <button
                      key={video.id}
                      type="button"
                      className={`playlist-video-card ${playerVideoId === video.id ? 'active' : ''}`}
                      onClick={() => handleVideoSelect(video.id)}
                    >
                      <div
                        className="playlist-video-thumb"
                        style={{ backgroundImage: `url(${selectImageFromThumbnails(video.thumbnails || {})})` }}
                      />
                      <div className="playlist-video-info">
                        <strong>{video.title}</strong>
                        <span>{formatDate(video.publishedAt)}</span>
                      </div>
                    </button>
                  ))
                ) : (
                  <p className="queue-empty">Pick a playlist to populate this list.</p>
                )}
              </div>
            </div>
          </aside>
        </div>
      </section>

      <section className={`youtube-video-section${isExploreOpen ? '' : ' collapsed-section'}`}>
        <header>
          <div>
            <h3>Browse all videos</h3>
            <p>Explore the full catalog beyond the playlist queue.</p>
          </div>
          <button
            type="button"
            className="panel-toggle"
            onClick={() => setIsExploreOpen((prev) => !prev)}
            aria-expanded={isExploreOpen}
          >
            {isExploreOpen ? 'Hide catalog' : 'Show catalog'}
          </button>
        </header>
        {emptyState ? (
          <div className="empty-state">
            <p>No videos match that query yet. Try clearing the search or picking a different playlist.</p>
          </div>
        ) : (
          <div className="video-board grid">
            {filteredVideos.map((video) => (
              <article
                key={video.id}
                className={`video-card ${playerVideoId === video.id ? 'active' : ''}`}
                onClick={() => handleVideoSelect(video.id)}
              >
                <div
                  className="video-thumb"
                  style={{ backgroundImage: `url(${selectImageFromThumbnails(video.thumbnails)})` }}
                />
                <div className="video-details">
                  <div>
                    <strong>{video.title}</strong>
                    <span>{formatDate(video.publishedAt)}</span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
      {bottomPanelOpen && (
        <div className="video-bottom-panel">
          <section className="youtube-control-bar">
            <div className="youtube-search">
              <FaSearch className="search-icon" />
              <input
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search playlists, artists, lyrics, tags..."
              />
            </div>
            <div className="playlist-select">
              <select
                value={selectedPlaylistId}
                onChange={(event) => setSelectedPlaylistId(event.target.value)}
              >
                <option value="">Random lineup</option>
                {playlistEntries.map((playlist) => (
                  <option key={playlist.id} value={playlist.id}>
                    {playlist.title}
                  </option>
                ))}
              </select>
            </div>
            <div className="player-controls">
              <button type="button" className="ghost" onClick={handlePrevious} aria-label="Previous video">
                <FaStepBackward />
              </button>
              <button type="button" className="ghost play-toggle" onClick={handlePlayPause} aria-label="Play or pause">
                {playerState === 1 ? <FaPause /> : <FaPlay />}
              </button>
              <button type="button" className="ghost" onClick={handleNext} aria-label="Next video">
                <FaStepForward />
              </button>
              {needsUserPlay && (
                <button
                  type="button"
                  className="ghost resume-control"
                  onClick={handleResumeAutoplay}
                  aria-label="Tap to play next"
                >
                  Tap to play next
                </button>
              )}
              <button type="button" className="ghost max-button" onClick={toggleFullscreen} aria-label="Toggle fullscreen view">
                <FaExpand />
              </button>
              <button
                type="button"
                className="ghost panel-close"
                onClick={() => setBottomPanelOpen(false)}
                aria-label="Hide bottom controls"
              >
                <FaTimes />
              </button>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
